
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import { buffer, extend, getWidth } from 'ol/extent';

import ContainerWithToolbar from '../../common/client/components/toolbar/ContainerWithToolbar';
import ToolbarSeparator from '../../common/client/components/toolbar/ToolbarSeparator';
import MapBase from '../../common/client/components/map/MapBase';
import { getLayerFromMap } from '../../common/client/components/map/openlayers/olUtils';
import ButtonCommandZoomIn from '../../common/client/components/map/ButtonCommandZoomIn';
import ButtonCommandZoomOut from '../../common/client/components/map/ButtonCommandZoomOut';
import ButtonCommandZoomDefault from '../../common/client/components/map/ButtonCommandZoomDefault';
import ButtonCommandTools from '../../common/client/components/map/ButtonCommandTools';
import LayerSwitcherButton from '../../common/client/components/map/LayerSwitcherButton';
import DialogConfirm from '../../common/client/components/DialogConfirm';

import IconDefault from '../../icons/map_tool_default.png';

import { getUsekMezItems } from '../../reducers/project';

import mapGetActions from '../../sagas/map';
import projectGetActions from '../../sagas/project';

import ButtonCommandZoomExtent from './ButtonCommandZoomExtent';
import MapView  from './Map.view';
import MAP_PROJECT from './mapProjectDef';
import DialogEditUsekAttr from './DialogEditUsekAttr';
import OlMapPlugin from './openlayers/OlMapPlugin';
import createInteractionDrawLine from './openlayers/interactionMapToolDrawLine';

const classNames = mergeStyleSets({
    layers: {
        position: 'absolute',
        zIndex: 100,
        right: 64,
        padding: 4
    }
});

const MapState = (
) => {

    const [mapTool, setMapTool] = React.useState(null);

    const dispatch = useDispatch();
    const mapActions = mapGetActions(dispatch);
    const projectActions = projectGetActions(dispatch);

    const map = useSelector(state => state.map);
    const usekMezItems = useSelector(state => getUsekMezItems(state.project));

    const olMapPlugin = useRef();

    useEffect(() => {
        mapActions.initProject(MAP_PROJECT);
        mapActions.addCommand(
            'EXEC_CODE',
            {
                callback: initMap
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        mapActions.addCommand(
            'EXEC_CODE',
            {
                callback: (map, params, olMap) => {
                    if (usekMezItems) {
                        let layer = getLayerFromMap(map, 'useky');
                        if (layer) {
                            layer.getSource().clear();
                            let geoJson = new GeoJSON();
                            usekMezItems.forEach(item => {
                                let feature = new Feature({
                                    geometry: geoJson.readGeometry(item.geometry),
                                    ...item.properties
                                });
                                layer.getSource().addFeature(feature);
                            });
                        }
                    }
                }
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usekMezItems]);

    const setTempFeature = (feature) => {
        mapActions.addCommand(
            'SET_TEMP_FEATURE',
            {
                features: feature ? [feature] : []
            }
        );
        mapActions.setTempFeature(feature);
    }

    const getExistingUsekId = () => {
        return (usekMezItems || []).map(item => item.properties.id_usek);
    }

    const handleMapToolExecute = async (toolId, params) => {
        switch (toolId) {
            case 'DrawLine':
                setTempFeature(params.feature);
                let result = await DialogEditUsekAttr.show('', false, getExistingUsekId());
                if (result && (result.result === 'OK') && result.attrs) {
                    projectActions.addUsek({
                        geometry: (new GeoJSON()).writeGeometry(params.feature.getGeometry()),
                        properties: {
                            id_usek: result.attrs.id_usek
                        }
                    });
                }
                setTempFeature(null);
                break;
            case 'ModifyFeature':
                projectActions.editUsek({
                    id_usek: params.feature.getProperties()['id_usek'],
                    geometry: (new GeoJSON()).writeGeometry(params.feature.getGeometry())
                });
                break;
            default:
                // nop;
                break;
        }
    }

    const handleSelectTool = (toolId, tool) => {
        setMapTool(toolId);
        mapActions.addCommand(
            'SET_MAP_TOOL',
            {
                toolId: toolId
            }
        );
    }

    const getMapTools = () => {
        const toolModifyFeature = {
            toolId: 'ModifyFeature',
            iconName: 'EditCreate',
            title: 'Upravit geometrii'
        };

        const toolDrawLine = {
            toolId: 'DrawLine',
            iconName: 'Edit',
            title: 'Nakreslit linii'
        };

        let tools = [{
            toolId: 'Default',
            iconSrc: IconDefault,
            title: 'Pohyb v mapě'
        }];

        tools.push(toolDrawLine);
        tools.push(toolModifyFeature);

        return tools;
    }

    const handleChangeView = (center, zoom, rotation, resolution) => {
        mapActions.changeView(center, zoom, rotation, resolution);
    }

    const handleFeatureClick = async (feature) => {
        if (mapTool === 'Default') {
            let id_usek_old = feature.getProperties()['id_usek'];
            let result = await DialogEditUsekAttr.show(id_usek_old, true, getExistingUsekId());
            if (result) {
                switch (result.result) {
                    case 'OK':
                        projectActions.changeUsekId(id_usek_old, result.attrs.id_usek);
                        break;
                    case 'DELETE':
                        let confirm = await DialogConfirm.show(`Opravdu chcete smazat úsek meze "${id_usek_old}" ?`);
                        if (confirm) {
                            projectActions.deleteUsek(id_usek_old);
                        }
                        break;
                    default:
                        // nop;
                        break;
                }
            }
        }
    }

    const initMap = (map, params, olMap) => {
        olMapPlugin.current = new OlMapPlugin({
            olMap: olMap
        });
        olMapPlugin.current.init();
        olMapPlugin.current.initInteractions(handleMapToolExecute);
        zoomToUsekMezItems(map);
    }

    const getUsekMezItemsExtent = () => {
        let extent;
        let geoJson = new GeoJSON();
        if (usekMezItems) {
            usekMezItems.forEach(item => {
                let geometry = geoJson.readGeometry(item.geometry);
                let extentGeom = geometry.getExtent();
                extent = (extent)
                    ? extend(extent, extentGeom)
                    : extentGeom;
            });
            if (extent) {
                extent = buffer(extent, getWidth(extent) / 2);
            }
        }
        return extent;
    }

    const zoomToUsekMezItems = (map) => {
        let extent = getUsekMezItemsExtent();
        if (extent) {
            map.getView().fit(extent);
        }
    }

    const mapButtons = [
        <ButtonCommandZoomIn/>,
        <ButtonCommandZoomOut/>,
        <ButtonCommandZoomDefault
            defaultExtent={MAP_PROJECT.extent}
        />,
        <ButtonCommandZoomExtent
            extent={getUsekMezItemsExtent()}
            title="Zobrazit všechny úseky mezí"
            icon="Search"
        />,
        <ToolbarSeparator />,
        <ButtonCommandTools
            tools={getMapTools()}
            onSelectTool={handleSelectTool}
        />
    ];

    const olInteractions = [
        createInteractionDrawLine
    ];

    const componentMap = (
        <ContainerWithToolbar toolbarPosition="right" toolbarDirection="" buttons={mapButtons}>
            <div className={classNames.layers}>
                <LayerSwitcherButton
                    map={map}
                    layers={map.layers}
                    groups={map.groups}
                    moduleActions={mapActions}
                />
            </div>
            <MapBase
                map={map}
                layers={map.layers}
                groups={map.groups}
                olInteractions={olInteractions}
                onChangeView={handleChangeView}
                onFeatureClick={handleFeatureClick}
                onMapToolExecute={handleMapToolExecute}
                moduleActions={mapActions}
            />
        </ContainerWithToolbar>
    );

    return map.definition && (
        <MapView
            componentMap={componentMap}
        />
    );
}

export default MapState;
